import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
    getSiteSettings,
    getHomepageSeo,
    getEnterprisePage,
    getHealthcarePage,
    getServicesPage,
    getServiceBySlug,
    getAboutPage,
    getContactPage,
    getPerspectivePage,
    getPostBySlug,
} from "../lib/sanityQueries";

const SITE_URL = "https://easilinked.com";

const normalizePath = (pathname) =>
    pathname === "/" ? "/" : pathname.replace(/\/+$/, "");

const canonicalForPath = (pathname) =>
    pathname === "/" ? `${SITE_URL}/` : `${SITE_URL}${pathname}`;

const routeLabel = (pathname, routeData) => {
    if (pathname === "/enterprise") return "Enterprise";
    if (pathname === "/healthcare-secure") return "Healthcare Secure™";
    if (pathname === "/services") return "Services";
    if (pathname === "/about") return "About";
    if (pathname === "/contact") return "Contact";
    if (pathname === "/perspective") return "The EasiLinked Perspective";
    if (pathname.startsWith("/services/")) {
        return routeData?.name || "Service";
    }
    return "EasiLinked";
};

const buildBreadcrumbs = (pathname, routeData) => {
    if (pathname === "/") return null;

    const items = [
        {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: `${SITE_URL}/`,
        },
    ];

    if (pathname.startsWith("/perspective/")) {
        items.push({
            "@type": "ListItem",
            position: 2,
            name: "The EasiLinked Perspective",
            item: `${SITE_URL}/perspective`,
        });

        items.push({
            "@type": "ListItem",
            position: 3,
            name: routeData?.title || "Post",
            item: canonicalForPath(pathname),
        });
    } else if (pathname.startsWith("/services/")) {
        items.push({
            "@type": "ListItem",
            position: 2,
            name: "Services",
            item: `${SITE_URL}/services`,
        });

        items.push({
            "@type": "ListItem",
            position: 3,
            name: routeData?.name || "Service",
            item: canonicalForPath(pathname),
        });
    } else {
        items.push({
            "@type": "ListItem",
            position: 2,
            name: routeLabel(pathname, routeData),
            item: canonicalForPath(pathname),
        });
    }

    return {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: items,
    };
};

const buildFaqPage = (faqs = []) => {
    if (!faqs.length) return null;

    return {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((faq) => ({
            "@type": "Question",
            name: faq.question,
            acceptedAnswer: {
                "@type": "Answer",
                text: faq.answer,
            },
        })),
    };
};

const setJsonLd = (id, payload) => {
    if (!payload) return null;

    let script = document.getElementById(id);
    const created = !script;

    if (!script) {
        script = document.createElement("script");
        script.id = id;
        script.type = "application/ld+json";
        document.head.appendChild(script);
    }

    script.textContent = JSON.stringify(payload);

    return { script, created };
};

const upsertMeta = (attr, key, value) => {
    if (!value) return null;

    let el = document.head.querySelector(`meta[${attr}="${key}"]`);
    const created = !el;
    const previous = el?.getAttribute("content");

    if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
    }

    el.setAttribute("content", value);

    return { el, created, previous };
};

export const StructuredData = () => {
    const location = useLocation();
    const pathname = normalizePath(location.pathname);

    const [settings, setSettings] = useState(null);
    const [routeData, setRouteData] = useState(null);

    useEffect(() => {
        let active = true;

        getSiteSettings()
            .then((data) => {
                if (active && data) setSettings(data);
            })
            .catch(() => {});

        return () => {
            active = false;
        };
    }, []);

    useEffect(() => {
        let active = true;
        setRouteData(null);

        let request = null;

        if (pathname === "/") {
            request = getHomepageSeo();
        } else if (pathname === "/enterprise") {
            request = getEnterprisePage();
        } else if (pathname === "/healthcare-secure") {
            request = getHealthcarePage();
        } else if (pathname === "/services") {
            request = getServicesPage();
        } else if (pathname === "/about") {
            request = getAboutPage();
        } else if (pathname === "/contact") {
            request = getContactPage();
        } else if (pathname.startsWith("/services/")) {
            const slug = pathname.slice("/services/".length);
            if (slug) request = getServiceBySlug(slug);
        } else if (pathname === "/perspective") {
            request = getPerspectivePage();
        } else if (pathname.startsWith("/perspective/")) {
            const slug = pathname.slice("/perspective/".length);
            if (slug) request = getPostBySlug(slug);
        }

        if (request) {
            request
                .then((data) => {
                    if (active && data) setRouteData(data);
                })
                .catch(() => {});
        }

        return () => {
            active = false;
        };
    }, [pathname]);

    useEffect(() => {
        if (!settings?.websiteUrl) return;

        const organization = {
            "@context": "https://schema.org",
            "@type": "Organization",
            "@id": `${SITE_URL}/#organization`,
            name: settings.legalBusinessName || settings.companyName,
            url: settings.websiteUrl,
            description: settings.businessDescription,
            email: settings.primaryEmail,
            telephone: settings.primaryPhone,
            sameAs: [settings.linkedinUrl, settings.facebookUrl].filter(Boolean),
        };

        const website = {
            "@context": "https://schema.org",
            "@type": "WebSite",
            "@id": `${SITE_URL}/#website`,
            url: settings.websiteUrl,
            name: settings.companyName,
            publisher: {
                "@id": `${SITE_URL}/#organization`,
            },
        };

        const seo = routeData?.seo;
        const canonicalUrl =
            seo?.canonicalUrl || canonicalForPath(pathname);

        const webPage = {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "@id": `${canonicalUrl}#webpage`,
            url: canonicalUrl,
            name:
                seo?.metaTitle ||
                routeLabel(pathname, routeData),
            description: seo?.metaDescription,
            isPartOf: {
                "@id": `${SITE_URL}/#website`,
            },
            about: {
                "@id": `${SITE_URL}/#organization`,
            },
        };

        const breadcrumb = buildBreadcrumbs(pathname, routeData);

        const faqPage =
            pathname === "/enterprise" ||
            pathname === "/healthcare-secure"
                ? buildFaqPage(routeData?.featuredFaqs || [])
                : null;

        const service =
            pathname.startsWith("/services/") && routeData?.name
                ? {
                      "@context": "https://schema.org",
                      "@type": "Service",
                      "@id": `${canonicalUrl}#service`,
                      name: routeData.name,
                      description:
                          routeData?.seo?.metaDescription ||
                          routeData.shortDescription ||
                          routeData.businessOutcome,
                      url: canonicalUrl,
                      provider: {
                          "@id": `${SITE_URL}/#organization`,
                      },
                  }
                : null;

        const jsonLdEntries = [
            setJsonLd("easilinked-organization-jsonld", organization),
            setJsonLd("easilinked-website-jsonld", website),
            setJsonLd("easilinked-webpage-jsonld", webPage),
            setJsonLd("easilinked-breadcrumb-jsonld", breadcrumb),
            setJsonLd("easilinked-faq-jsonld", faqPage),
            setJsonLd("easilinked-service-jsonld", service),
        ].filter(Boolean);

        // Enterprise and Healthcare still use their legacy page-specific SEO
        // hooks, so add the social metadata they do not emit yet.
        const socialMeta = [];

        if (
            pathname === "/enterprise" ||
            pathname === "/healthcare-secure"
        ) {
            const socialTitle =
                seo?.socialTitle || seo?.metaTitle;

            const socialDescription =
                seo?.socialDescription || seo?.metaDescription;

            socialMeta.push(
                upsertMeta("property", "og:type", "website"),
                upsertMeta("property", "og:title", socialTitle),
                upsertMeta(
                    "property",
                    "og:description",
                    socialDescription
                ),
                upsertMeta("property", "og:url", canonicalUrl),
                upsertMeta("name", "twitter:card", "summary"),
                upsertMeta("name", "twitter:title", socialTitle),
                upsertMeta(
                    "name",
                    "twitter:description",
                    socialDescription
                )
            );
        }

        return () => {
            jsonLdEntries.forEach(({ script, created }) => {
                if (created) script.remove();
            });

            socialMeta.filter(Boolean).forEach(
                ({ el, created, previous }) => {
                    if (created) {
                        el.remove();
                    } else if (
                        previous !== null &&
                        previous !== undefined
                    ) {
                        el.setAttribute("content", previous);
                    }
                }
            );
        };
    }, [settings, routeData, pathname]);

    return null;
};
